import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { API_BASE_URL } from "../../environment";
import { SelectOption, Tag, TagsDiff } from "./types";

type InitialTagsInput = Tag[] | { insert?: string[] } | null | undefined;

interface UseTagsFieldOptions {
  debounceMs?: number;
}

interface UseTagsFieldResult {
  tags: Tag[];
  setTags: (tags: Tag[]) => void;
  availableTags: SelectOption[];
  isSearchingTags: boolean;
  onInputChange: (input: string) => void;
  onChange: (newValue: any) => void;
  isValidNewOption: (
    inputValue: string,
    selectValue: any,
    selectOptions: any
  ) => boolean;
  resetTo: (tags: InitialTagsInput) => void;
  getDiff: () => TagsDiff;
}

const normalizeTags = (initial: InitialTagsInput): Tag[] => {
  if (!initial) return [];

  const tagData = Array.isArray(initial) ? initial : initial.insert || [];

  return (tagData as any[]).map((tag: any) => {
    if (typeof tag === "string") return { entity_tag_name: tag };
    return {
      entity_tag_id: tag.entity_tag_id,
      entity_tag_name: tag.entity_tag_name,
    };
  });
};

export function useTagsField(
  initialTags: InitialTagsInput,
  options?: UseTagsFieldOptions
): UseTagsFieldResult {
  const debounceMs = options?.debounceMs ?? 1000;

  const [tags, setTagsState] = useState<Tag[]>(() => normalizeTags(initialTags));
  const [originalTags, setOriginalTags] = useState<Tag[]>(() =>
    normalizeTags(initialTags)
  );
  const [availableTags, setAvailableTags] = useState<SelectOption[]>([]);
  const [isSearchingTags, setIsSearchingTags] = useState(false);

  const debouncedFetchTagsRef = useRef<ReturnType<typeof debounce> | null>(
    null
  );

  useEffect(() => {
    const fetchTags = debounce(async (inputValue: string) => {
      if (!inputValue) {
        setAvailableTags([]);
        return;
      }

      setIsSearchingTags(true);
      try {
        const authHeader = localStorage.getItem("authCred");
        const response = await fetch(
          `${API_BASE_URL}/tags/?entity_tag_name=${inputValue}`,
          {
            headers: { Authorization: `Basic ${authHeader}` },
          }
        );
        const data = await response.json();

        const opts: SelectOption[] = (Array.isArray(data) ? data : []).map(
          (tag: any) => ({
            value: tag.entity_tag_name,
            label: tag.entity_tag_name,
          })
        );

        setAvailableTags(opts);
      } catch (err) {
        console.error("Failed to fetch tags", err);
      } finally {
        setIsSearchingTags(false);
      }
    }, debounceMs);

    debouncedFetchTagsRef.current = fetchTags;

    return () => {
      fetchTags.cancel();
    };
  }, [debounceMs]);

  const setTags = useCallback((next: Tag[]) => {
    setTagsState(next);
  }, []);

  const resetTo = useCallback((next: InitialTagsInput) => {
    const normalized = normalizeTags(next);
    setTagsState(normalized);
    setOriginalTags(normalized);
  }, []);

  const onInputChange = useCallback((input: string) => {
    debouncedFetchTagsRef.current?.(input);
  }, []);

  const onChange = useCallback((newValue: any) => {
    const val = newValue || [];

    setTagsState((prevTags) => {
      const uniqueTags: Tag[] = [];
      const seenNames = new Set<string>();

      val.forEach((opt: any) => {
        const capitalizedLabel =
          opt.label.trim().charAt(0).toUpperCase() + opt.label.trim().slice(1);
        const lowerName = capitalizedLabel.toLowerCase();

        if (!seenNames.has(lowerName)) {
          seenNames.add(lowerName);
          const existing = prevTags.find(
            (t) => t.entity_tag_name.toLowerCase() === lowerName
          );
          uniqueTags.push({
            entity_tag_id: existing?.entity_tag_id,
            entity_tag_name: capitalizedLabel,
          });
        }
      });

      return uniqueTags;
    });

    setAvailableTags([]);
  }, []);

  const isValidNewOption = useCallback(
    (inputValue: string, _selectValue: any, _selectOptions: any) => {
      const exists = tags.some(
        (tag) => tag.entity_tag_name.toLowerCase() === inputValue.toLowerCase()
      );
      return inputValue.trim() !== "" && !exists;
    },
    [tags]
  );

  const getDiff = useCallback((): TagsDiff => {
    const insert = tags
      .filter(
        (tag) =>
          !originalTags.some(
            (ot) => ot.entity_tag_name === tag.entity_tag_name
          )
      )
      .map((tag) => tag.entity_tag_name);

    const del = originalTags
      .filter(
        (ot) =>
          ot.entity_tag_id !== undefined &&
          !tags.some((tag) => tag.entity_tag_name === ot.entity_tag_name)
      )
      .map((ot) => ot.entity_tag_id as number);

    return { insert, delete: del };
  }, [tags, originalTags]);

  return {
    tags,
    setTags,
    availableTags,
    isSearchingTags,
    onInputChange,
    onChange,
    isValidNewOption,
    resetTo,
    getDiff,
  };
}
